import { logger } from '@magicschool/logger';
import type { Enums } from '@magicschool/supabase/types';
import type { ImageUrlMap, SignedImageUrl } from 'features/assistant/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { ImmutableMap } from 'util/immutable';
import { validate as isValidUUID } from 'uuid';
import { summarizeThreadByTeacherServerAction } from '@/app/(student)/s/(protected)/rooms/[roomId]/(unlocked)/tools/[roomToolId]/actions';
import type {
  RoomStudentsOutputMonitoringResponse,
  SlimAssistantThreadSummary,
  StudentThread,
} from '@/app/api/room-students/[id]/output/route';
import type { StudentWithDetails } from '@/app/api/rooms/[id]/students/route';
import type { RoomTeacherUser } from '@/app/api/rooms/[id]/teachers/route';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import {
  fetchRoomStudentSummaryServerAction,
  fetchThreadSummaryServerAction,
  getRoomStudentByStudentIdServerAction,
  triggerStudentSummarizationServerAction,
} from './actions';

export type Student = StudentWithDetails;

export type RoomDetailsStore = {
  loadingThreads: boolean;
  coTeacherDrawerOpen: boolean;
  threadDrawerOpen: boolean;
  selectedStudent: Student | null;
  studentThreads: StudentThread[] | null;
  summaryLoading: boolean;
  confirmRemoveModalOpen: boolean;
  studentToDelete: Student | null;
  deleteThreadModalOpen: boolean;
  threadToDelete: number | null;
  removeCoTeacherModalOpen: boolean;
  coTeacherToRemove: RoomTeacherUser | null;
  moderationDrawerOpen: boolean;
  imageUrlMap: ImageUrlMap;
  selectedStudentSummary: RoomStudentsOutputMonitoringResponse['summary'];
  roomToolIdToDelete: string | null;
  updatingRoomDetails: boolean;
  studentTableRef: React.RefObject<HTMLTableElement | null> | null;
  coTeacherDrawerTitleRef: React.RefObject<HTMLElement | null> | null;
  targetMessageId: number | null;
  loadStudentContent: (studentId: string | undefined | null, roomId: string | undefined | null) => Promise<void>;
  setField: SetField<RoomDetailsStore>;
  getStudentThreads: (student: Student) => Promise<void>;
  openCoTeachersDrawer: () => void;
  onStudentMessageDeleted: (messageId: number) => void;
  onThreadDeleted: (threadId: number) => void;
  handleNewThreadSummary: (studentUserId: string, threadId: number, status: Enums<'thread_summary_status'>) => void;
  handleNewRoomStudentSummary: (studentUserId: string) => void;
  triggerStudentSummarization: (roomId?: string) => void;
  updateThreadSummary: (roomStudentId: string, threadId: number) => void;
  triggerThreadSummary: (threadId: number, messageId: number, toolTitle: string, toolCustomizationId: string | undefined) => void;
  threadUpdatesMap: ImmutableMap<number, boolean>;
};

// used to make types a bit easier, specifically when triggering a summary when
// an existing summary didn't exist, and needing a loading state.
const emptyAssistantThreadSummary = (): SlimAssistantThreadSummary => ({
  id: 0,
  content: '',
  engagement_level: 'distracted',
  updated_at: '',
  status: 'pending',
  assistant_thread_id: 0,
  room_student_id: '',
  learning_goal_progress: 'not_enough_info',
  learning_goal: '',
});

const defaultState = {
  loadingThreads: false,
  coTeacherDrawerOpen: false,
  threadDrawerOpen: false,
  selectedStudent: null,
  studentThreads: null,
  confirmRemoveModalOpen: false,
  studentToDelete: null,
  deleteThreadModalOpen: false,
  threadToDelete: null,
  removeCoTeacherModalOpen: false,
  coTeacherToRemove: null,
  moderationDrawerOpen: false,
  summaryLoading: false,
  imageUrlMap: ImmutableMap<string, SignedImageUrl>(),
  selectedStudentSummary: null,
  threadUpdatesMap: ImmutableMap<number, boolean>(),
  roomToolIdToDelete: null,
  updatingRoomDetails: false,
  studentTableRef: null,
  coTeacherDrawerTitleRef: null,
  targetMessageId: null,
};

export const createRoomDetailsStoreSlice = createStoreSlice('RoomDetailsStoreData', defaultState, ({ set, get, setField }) => ({
  setField,
  getStudentThreads: async (student) => {
    set({
      selectedStudent: student,
      selectedStudentSummary: null,
      threadDrawerOpen: true,
      loadingThreads: true,
      studentThreads: [],
      summaryLoading: false,
      threadUpdatesMap: ImmutableMap<number, boolean>(),
    });
    const response = await fetch(`/api/room-students/${student.id}/output`);
    const data: RoomStudentsOutputMonitoringResponse = await response.json();
    set({
      studentThreads: data.roomStudentThreadsWithMessages,
      imageUrlMap: ImmutableMap(data.imageUrlMap),
      loadingThreads: false,
      selectedStudentSummary: data.summary,
    });
  },
  loadStudentContent: async (studentId, roomId) => {
    if (!studentId || !isValidUUID(studentId) || !roomId || !isValidUUID(roomId)) return;
    const roomStudent = await getRoomStudentByStudentIdServerAction(studentId, roomId);
    if (roomStudent.data) {
      get().getStudentThreads({
        ...roomStudent.data,
        name: roomStudent.data.students.users.name ?? '',
        email: roomStudent.data.students.users.email ?? '',
        user_id: roomStudent.data.students.users.id,
        isPreviewStudent: Boolean(roomStudent.data.students.preview_teacher_id),
        most_recent_room_student_messages: [],
        flaggedMessageCount: 0,
      });
    } else {
      // We don't really need the user to see this error as it's likely a URL hack attempt
      logger.error('Error loading student content', roomStudent.error);
    }
  },
  openCoTeachersDrawer: () => {
    set({ coTeacherDrawerOpen: true });
  },
  onStudentMessageDeleted: (messageId) => {
    set((state) => {
      const thread = state.studentThreads?.find((thread) =>
        thread.assistant_threads.assistant_thread_messages.find((m) => m.id === messageId),
      );

      if (!thread) return {};

      thread.assistant_threads.assistant_thread_messages = thread.assistant_threads.assistant_thread_messages.filter(
        (m) => m.id !== messageId,
      );

      const withoutThread = state.studentThreads?.filter((t) => t.id !== thread.id) ?? [];
      withoutThread.push(thread);

      analyticsTrack('StudentRooms:DeleteThreadMessage', {
        threadId: thread.assistant_threads.id,
        messageId,
        userId: thread.assistant_threads.assistant_thread_messages[0].user_id,
        state: 'confirmed',
      });

      return {
        studentThreads: withoutThread,
      };
    });
  },
  onThreadDeleted: (threadId) => {
    set((state) => ({
      studentThreads: state.studentThreads?.filter((t) => t.assistant_threads.id !== threadId) ?? null,
      threadUpdatesMap: state.threadUpdatesMap.delete(threadId),
    }));
  },
  updateThreadSummary: async (roomStudentId: string, threadId: number) => {
    const response = await fetchThreadSummaryServerAction(roomStudentId, threadId);
    if (response.error || !response.data) return;

    set((state) => {
      const thread = state.studentThreads?.find((t) => t.assistant_threads.id === threadId);
      if (!thread) return {};
      thread.assistant_threads.summary = response.data;
      return {
        studentThreads: state.studentThreads?.filter((t) => t.assistant_threads.id !== threadId).concat(thread),
        threadUpdatesMap: state.threadUpdatesMap.set(threadId, false),
      };
    });
  },
  triggerThreadSummary: async (threadId, messageId, toolTitle, toolCustomizationId) => {
    const { selectedStudent } = get();
    if (!selectedStudent) return;

    await summarizeThreadByTeacherServerAction(
      selectedStudent.user_id ?? '',
      selectedStudent.id,
      selectedStudent.name,
      threadId,
      messageId,
      selectedStudent.room_id,
      toolTitle,
      toolCustomizationId,
    );
    set((state) => ({ threadUpdatesMap: state.threadUpdatesMap.set(threadId, true) }));
  },
  handleNewThreadSummary: (studentUserId, threadId, status) => {
    const { studentThreads, selectedStudent } = get();

    // if they don't have the output drawer open, we don't do anything
    if (selectedStudent?.user_id !== studentUserId) return;

    const existingSummaryAvailable = studentThreads?.find((t) => t.assistant_threads.id === threadId)?.assistant_threads.summary?.content;
    if (!existingSummaryAvailable) {
      // we don't have a summary for this thread yet, so it's okay to pop it in place in the ui.
      get().updateThreadSummary(selectedStudent.id, threadId);
      return;
    }

    set((state) => {
      const thread = state.studentThreads?.find((t) => t.assistant_threads.id === threadId);
      if (!thread) return {};
      thread.assistant_threads.summary = thread.assistant_threads.summary
        ? { ...thread.assistant_threads.summary, status }
        : emptyAssistantThreadSummary();

      return {
        studentThreads: state.studentThreads?.filter((t) => t.assistant_threads.id !== threadId).concat(thread),
        threadUpdatesMap: state.threadUpdatesMap.set(threadId, true),
      };
    });
  },
  triggerStudentSummarization: async (roomId) => {
    const roomStudentId = get().selectedStudent?.id;
    if (!roomStudentId || !roomId) return;
    set({ summaryLoading: true });
    await triggerStudentSummarizationServerAction(roomStudentId, roomId);
  },
  handleNewRoomStudentSummary: async (studentUserId) => {
    set({ summaryLoading: false });
    const selectedStudent = get().selectedStudent;
    if (selectedStudent?.user_id !== studentUserId) {
      return;
    }

    const summary = await fetchRoomStudentSummaryServerAction(selectedStudent.id);
    if (summary.error) return;
    if (get().selectedStudent?.id === selectedStudent.id) {
      set({ selectedStudentSummary: summary.data });
    }
  },
}));
